/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, TrendingUp } from 'lucide-react';
import ConfidenceChip from '@/components/terra/ConfidenceChip';

/**
 * Friction Matrix™ view.
 *
 * Strategic command-center grade. Answers four questions per cell:
 *   • How many leads currently sit here?
 *   • How many are stalling (over the calm threshold)?
 *   • How fast does this rung release them forward?
 *   • Should we trust this number? (confidence chip mandatory)
 *
 * Calm tones: stone → amber → emerald.  Never red.
 */
export default function FrictionMatrixPage() {
  const [data, setData] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [m, t, e] = await Promise.all([
          axios.get(`${API}/terra-growth/intelligence/friction-matrix?days=${days}`),
          axios.get(`${API}/terra-growth/intelligence/thresholds`),
          axios.get(`${API}/terra-growth/ecosystems`),
        ]);
        setData(m.data);
        setThresholds(t.data);
        setEcosystems(e.data?.ecosystems || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [days]);

  const ecoMap = useMemo(() => ecosystems.reduce((m, e) => { m[e.code] = e; return m; }, {}), [ecosystems]);

  // Build a 2-D grid keyed by ecosystem then state, for matrix rendering.
  const grid = useMemo(() => {
    if (!data) return null;
    const ecos = Array.from(new Set(data.rows.map(r => r.ecosystem_code)));
    const states = data.perception_states || [];
    const cells = {};
    data.rows.forEach(r => { cells[`${r.ecosystem_code}|${r.perception_state}`] = r; });
    return { ecos, states, cells };
  }, [data]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="friction-matrix-page" className="max-w-7xl mx-auto px-6 py-8">
        <Header days={days} onDaysChange={setDays} />

        {loading ? (
          <Skeleton />
        ) : !data || data.rows.length === 0 ? (
          <Empty />
        ) : (
          <>
            <Legend thresholds={thresholds} />
            <Matrix grid={grid} ecoMap={ecoMap} labels={data.perception_labels} />
            <FrictionLegendFooter thresholds={thresholds} />
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Header({ days, onDaysChange }) {
  return (
    <div className="mb-6">
      <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
      <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Perception Flow Friction Matrix™</h1>
      <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
        Where is perception moving, and where is it stuck? Each cell shows current size, stall density,
        and forward velocity for the (ecosystem × perception state) pair.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">Window</span>
        {[60, 90, 180].map(d => (
          <button key={d}
                  data-testid={`friction-days-${d}`}
                  onClick={() => onDaysChange(d)}
                  className={`text-[11px] rounded-full px-3 py-1 border transition-all ${
                    days === d
                      ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]'
                      : 'bg-white text-[#5C5955] border-[#D8D4CE]'
                  }`}>{d}d</button>
        ))}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing matrix…
    </div>
  );
}

function Empty() {
  return (
    <div data-testid="friction-matrix-empty"
         className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      No leads tagged yet — nothing to compute. Add leads to ecosystems first.
    </div>
  );
}

function Legend({ thresholds }) {
  if (!thresholds) return null;
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
      <Tile label="Stuck thresholds"
            note="Cell is stalled if no transition logged for: awareness 14d · evaluation 21d · CONVERSION_READY 10d." />
      <Tile label="Forward velocity"
            note="Share of in-window transitions that moved the lead UP the ladder." />
      <Tile label="Confidence"
            note="Each cell carries: Low <20 transitions · Emerging 20–49 · Reliable 50+." />
    </div>
  );
}

function Tile({ label, note }) {
  return (
    <div className="rounded-xl border border-[#EBE8E3] bg-[#FBFaf9] p-3">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8C6239]">{label}</p>
      <p className="text-[11px] text-[#5C5955] mt-1 leading-snug">{note}</p>
    </div>
  );
}

function Matrix({ grid, ecoMap, labels }) {
  if (!grid) return null;
  return (
    <div data-testid="friction-matrix-grid"
         className="rounded-2xl border border-[#EBE8E3] bg-white overflow-x-auto">
      <table className="w-full text-xs font-['Work_Sans']">
        <thead className="bg-[#F4F2EE] text-[10px] uppercase tracking-[0.14em] text-[#8A8681]">
          <tr>
            <th className="px-3 py-2 text-left sticky left-0 bg-[#F4F2EE]">Ecosystem</th>
            {grid.states.map(s => (
              <th key={s} className="px-2 py-2 text-left whitespace-nowrap">
                {labels[s] || s}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.ecos.map(eco => (
            <tr key={eco} data-testid={`friction-row-${eco}`} className="border-t border-[#EBE8E3]">
              <td className="px-3 py-2 sticky left-0 bg-white border-r border-[#EBE8E3] whitespace-nowrap">
                <div className="text-[#2C2A29]">{ecoMap[eco]?.name || (eco === '_UNTAGGED' ? '— untagged —' : eco)}</div>
              </td>
              {grid.states.map(s => {
                const cell = grid.cells[`${eco}|${s}`];
                return (
                  <td key={s} className="px-2 py-2 align-top">
                    <Cell cell={cell} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ cell }) {
  if (!cell || (cell.leads_in_state === 0 && cell.transitions === 0)) {
    return <div className="text-[#D8D4CE] text-[11px]">—</div>;
  }
  const stuck = cell.stuck_pct || 0;
  const stuckTone =
      stuck >= 60 ? 'text-rose-700 bg-rose-50 border-rose-200'
    : stuck >= 30 ? 'text-amber-800 bg-amber-50 border-amber-300'
    :              'text-stone-600 bg-stone-50 border-stone-200';
  const vel = cell.transition_velocity_pct;
  return (
    <div data-testid={`friction-cell-${cell.ecosystem_code}-${cell.perception_state}`}
         className="space-y-1.5 min-w-[120px]">
      <div className="flex items-center gap-1.5">
        <span className="text-sm tabular-nums text-[#2C2A29]">{cell.leads_in_state}</span>
        <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8681]">in state</span>
      </div>
      <div className={`text-[10px] tabular-nums rounded border px-1.5 py-0.5 inline-block ${stuckTone}`}>
        {stuck}% stalled
      </div>
      {vel !== null && vel !== undefined && (
        <div className="flex items-center gap-1 text-[10px] text-[#4A5D4E]">
          <TrendingUp className="w-2.5 h-2.5" /> {vel}% forward
        </div>
      )}
      <ConfidenceChip code={cell.confidence} sample={cell.transitions} compact />
    </div>
  );
}

function FrictionLegendFooter() {
  return (
    <p className="mt-3 text-[10px] text-[#8A8681] uppercase tracking-[0.14em] font-['Work_Sans']">
      Each cell answers: where is perception moving, where is it stuck, which ecosystem is resisting?
    </p>
  );
}
